import type {
  AlarmDeviceTarget,
  AlarmTargetableDevice,
  BessEnergyHistoryQuery, BessEnergyHistoryResponse, BessEnergySample,
  EmsAdapter, EnergyGran, InspectableDevice,
  ZoneAsset, ZoneAttributeEntry, ZoneTsPoint,
} from "./adapter";
import { MockSite } from "./mockGenerator";
import {
  parseAlarmRuleEnvelope,
  type AlarmRule,
  type AlarmRuleEnvelope,
  type AlarmRulesStatus,
  type AvailableDatapointsEnvelope,
  type DatapointInfo,
} from "./alarmRules/wireFormat";
import type {
  AggregateBucket,
  Alarm,
  AlarmComment,
  ControllerMode,
  HistoricalQuery,
  HistoricalResponse,
  OverridePayload,
  PricePoint,
  ScheduleDoc,
  ScheduleEntry,
  ScheduleSource,
  ScheduleStatus,
  SiteSnapshot,
  SiteTopology,
} from "./types";

export class MockAdapter implements EmsAdapter {
  /** UI hides/disables what the backend can't do — never placebo (ALM-03).
   *  The mock implements the full alarm surface in-memory. */
  readonly capabilities = { snoozeAlarms: true, alarmComments: true };
  private site: MockSite;
  private listeners = new Set<(s: SiteSnapshot) => void>();
  private timer: number | null = null;
  /** Wall-clock ms when the simulated telemetry outage began, or null
   *  when healthy. Debug-only (see {@link setOutage}). */
  private outageStartedAt: number | null = null;
  /** Per-device alarm-rule state. Simulated edge ACK lands ~800 ms after save. */
  private mockRules = new Map<
    string,
    { envelope: AlarmRuleEnvelope; status: AlarmRulesStatus | null }
  >();

  constructor(topology: SiteTopology, private tickMs = 1000) {
    this.site = new MockSite(topology);
    this.seedAlarmRules();
  }

  topology() { return this.site.topology; }

  /** Returns ~3 minutes of synthetic snapshots so the spark chart isn't flat
   *  on first render. */
  recent(): SiteSnapshot[] {
    return this.site.recent(60, 1000);
  }
  /** Same shape as the contract — mock generates ~60 min of synthetic
   *  snapshots in-memory (already does this in `recent()` above). */
  async recentHistory(): Promise<SiteSnapshot[]> {
    return this.site.recent(60, 1000);
  }

  /** Mock topology mirrors the live one with synthetic device ids so
   *  the operator can exercise the "Browse all telemetry" panel
   *  without a live EMS connection. The history() endpoint serves the
   *  same device-keyed lookups from the in-memory snapshot generator. */
  /** Synthesise plausible per-BESS energy buckets so the energy
   *  browser is exercisable in mock mode without a live EMS. */
  async bessEnergyHistory(opts: BessEnergyHistoryQuery): Promise<BessEnergyHistoryResponse> {
    const stepMs = bucketStep(opts.granularity);
    const allIds = this.site.topology.bess.map((b) => b.id);
    const want = new Set(opts.bessIds ?? allIds);
    const perBess: Record<string, BessEnergySample[]> = {};
    for (const bessId of allIds) {
      if (!want.has(bessId)) continue;
      const cap = this.site.topology.bess.find((b) => b.id === bessId)?.capacity_kWh ?? 200;
      const samples: BessEnergySample[] = [];
      // Walk bucket boundaries from start to end. Charge by day, discharge
      // by night with a smaller weekend dip. Magnitude scales with capacity.
      //
      // Align bucket starts to a step boundary (mirroring TB's calendar-aligned
      // EP_<g>/EN_<g> aggregation) so the trailing bucket is the CURRENT,
      // still-filling period — `now` lands strictly inside it, exactly as on a
      // live site. A rolling window starting at `start_ts` would instead end
      // flush at `now`, so the demo would never show the partial-bucket marker
      // the real charts rely on. The phase-aligning floor can place the first
      // boundary BEFORE start_ts; that pre-window bucket is dropped below so
      // the KPI totals match what a live TB query (in-window buckets only)
      // would return — the mock stays a faithful demo.
      const firstBucket = Math.floor(opts.start_ts / stepMs) * stepMs;
      for (let t = firstBucket; t < opts.end_ts; t += stepMs) {
        const d = new Date(t);
        const hour = d.getHours();
        const dow = d.getDay();
        const weekend = dow === 0 || dow === 6;
        const baseline = cap * 0.05 * (weekend ? 0.6 : 1);
        const dayRamp = Math.max(0, Math.sin(((hour - 6) / 12) * Math.PI));   // 0 at 06:00/18:00, 1 at noon
        const charge = baseline * (0.6 + dayRamp * 1.6);
        const discharge = baseline * (0.4 + (1 - dayRamp) * 1.4);
        // Scale per-bucket by relative window span so 1y buckets aren't tiny
        const scale = Math.max(1, stepMs / (3600_000));
        samples.push({
          ts: t,
          charged_kWh: Math.round(charge * scale * 10) / 10,
          discharged_kWh: Math.round(discharge * scale * 10) / 10,
        });
      }
      // Drop the phase-aligning bucket(s) that start before the requested
      // window so totals match a live in-window TB query (see note above).
      perBess[bessId] = samples.filter((s) => s.ts >= opts.start_ts);
    }
    return { granularity: opts.granularity, perBess };
  }

  async inspectableSeries(): Promise<InspectableDevice[]> {
    const topo = this.site.topology;
    const out: InspectableDevice[] = [];
    out.push({
      id: "mock-grid", kind: "grid", label: "Grid coupling",
      keys: [
        { key: "device:mock-grid:active_W", label: "Active power", unit: "kW", scale: 0.001, chart: "line" },
        { key: "device:mock-grid:reactive_VAr", label: "Reactive power", unit: "kVAr", scale: 0.001, chart: "line" },
      ],
    });
    topo.bess.forEach((b, idx) => {
      const friendly = b.name || `Battery #${idx + 1}`;
      out.push({
        id: `mock-bess-${b.id}`, kind: "bess", label: friendly,
        keys: [
          { key: `device:mock-bess-${b.id}:active_W`, label: "Active power", unit: "kW", scale: 0.001, chart: "line" },
          { key: `device:mock-bess-${b.id}:soc_pct`, label: "State of charge", unit: "%", chart: "line" },
          { key: `device:mock-bess-${b.id}:total_voltage_V`, label: "DC bus voltage", unit: "V", chart: "line" },
          { key: `device:mock-bess-${b.id}:ambient_temperature_C`, label: "Ambient temperature", unit: "°C", chart: "line" },
        ],
      });
    });
    return out;
  }

  subscribe(handler: (snap: SiteSnapshot) => void) {
    this.listeners.add(handler);
    if (this.timer == null) this.start();
    handler(this.snapshotNow());
    return () => {
      this.listeners.delete(handler);
      if (this.listeners.size === 0) this.stop();
    };
  }

  latest() { return this.snapshotNow(); }

  /** True while the simulated telemetry outage is active (DebugOverlay). */
  get outageActive(): boolean { return this.outageStartedAt !== null; }

  /**
   * Debug-only telemetry-outage simulation — deliberately NOT on the
   * EmsAdapter interface; the DebugOverlay feature-detects it. While on,
   * the tick loop keeps notifying listeners but the simulation stops
   * advancing: MockSite.nowOverride pins synthetic time at the outage
   * start, so values AND snapshot.ts freeze exactly like a real uplink
   * stall. The emitted connection degrades to "stale" immediately and
   * "offline" after 60 real seconds (see {@link snapshotNow}); turning
   * the outage off resumes the sim and connection returns "live".
   */
  setOutage(on: boolean): void {
    if (on === (this.outageStartedAt !== null)) return;
    if (on) {
      this.outageStartedAt = Date.now();
      this.site.nowOverride = this.outageStartedAt;
    } else {
      this.outageStartedAt = null;
      this.site.nowOverride = undefined;
    }
    // Reflect the flip immediately instead of waiting for the next tick.
    const snap = this.snapshotNow();
    for (const h of this.listeners) h(snap);
  }

  /** Fresh snapshot with the simulated-outage state stamped on
   *  (pass-through when healthy). */
  private snapshotNow(): SiteSnapshot {
    if (this.outageStartedAt === null) return this.site.snapshot();
    // Re-assert the freeze on every read: recent()/recentHistory() juggle
    // MockSite.nowOverride internally and reset it to undefined when
    // done, which would otherwise silently un-freeze the outage.
    this.site.nowOverride = this.outageStartedAt;
    const snap = this.site.snapshot();
    const outageAgeS = (Date.now() - this.outageStartedAt) / 1000;
    snap.connection = outageAgeS > 60 ? "offline" : "stale";
    // Keep the health block coherent with the degraded link so the
    // banner/uplink surfaces react the way they would on a real site.
    snap.health.cloud_uplink = snap.connection;
    snap.health.last_uplink_age_s = Math.round(outageAgeS);
    return snap;
  }

  history(query: HistoricalQuery): Promise<HistoricalResponse> {
    return Promise.resolve(this.site.history(query));
  }
  prices(start_ts: number, hours: number): Promise<PricePoint[]> {
    return Promise.resolve(this.site.priceCurve(start_ts, hours));
  }
  buckets(start_ts: number, end_ts: number, gran: "h" | "d" | "w" | "m"): Promise<AggregateBucket[]> {
    return Promise.resolve(this.site.buckets(start_ts, end_ts, gran));
  }

  async alarms(): Promise<Alarm[]> {
    return [...this.site.alarms].sort((a, b) => b.raised_at - a.raised_at);
  }

  async alarmComments(id: string): Promise<AlarmComment[]> {
    const a = this.site.alarms.find((x) => x.id === id);
    return a ? [...a.comments] : [];
  }

  bessIdForAlarm(alarm: Alarm): string | null {
    // Mock alarms carry the topology bess id directly (mockGenerator).
    const topo = this.site.topology;
    if (topo.bess.some((b) => b.id === alarm.device_id)) return alarm.device_id;
    return topo.bess.find((b) => b.name === alarm.device_name)?.id ?? null;
  }

  deviceTargetForAlarm(alarm: Alarm): AlarmDeviceTarget | null {
    // BESS first — keep the existing /bess/<id> drill (label unchanged).
    const bessId = this.bessIdForAlarm(alarm);
    if (bessId) return { kind: "bess", route: `/bess/${bessId}`, labelKey: "alarms.openDevice" };
    const topo = this.site.topology;
    // Grid meter (role=grid smartmeter) → the grid_W trace contextualises a
    // grid-stale/overpower alarm far better than the read-only /site config.
    const gridMeter = topo.smartmeters.find((m) => m.role === "grid");
    if (gridMeter && (alarm.device_id === gridMeter.id || alarm.device_name === gridMeter.name)) {
      return { kind: "grid", route: "/historical", labelKey: "alarms.viewMeter" };
    }
    // Controller / EMS health alarm → the controller page.
    if (alarm.device_id === topo.controller.id) {
      return { kind: "controller", route: "/controller", labelKey: "alarms.openController" };
    }
    // Nothing maps — keep only "View in Historical" (honesty fallback).
    return null;
  }

  historicalKeysFor(unitId: string, catalogKeys: string[]): string[] {
    // Catalog key → the raw key the mock device publishes (see the
    // per-BESS entries in inspectableSeries). Unmapped keys are dropped.
    const RAW: Record<string, string> = {
      ac_active_power: "active_W",
      soc: "soc_pct",
      total_voltage: "total_voltage_V",
      ambient_temperature: "ambient_temperature_C",
    };
    return catalogKeys.flatMap((k) =>
      RAW[k] ? [`device:mock-bess-${unitId}:${RAW[k]}`] : [],
    );
  }

  async ackAlarm(id: string, by: string) {
    const a = this.site.alarms.find((x) => x.id === id);
    if (a) { a.status = "acknowledged"; a.ack_at = Date.now(); a.ack_by = by; }
  }
  async clearAlarm(id: string, by: string) {
    const a = this.site.alarms.find((x) => x.id === id);
    if (a) {
      a.status = "cleared";
      a.cleared_at = Date.now();
      a.ack_at = a.ack_at ?? Date.now();
      a.ack_by = a.ack_by ?? by;
    }
  }
  async commentAlarm(id: string, by: string, text: string) {
    const a = this.site.alarms.find((x) => x.id === id);
    if (a) a.comments.push({ id: `c-${Math.random().toString(36).slice(2, 9)}`, at: Date.now(), by, text });
  }
  async snoozeAlarm(id: string, by: string, until: number) {
    const a = this.site.alarms.find((x) => x.id === id);
    if (a) {
      a.snoozed_until = until;
      a.comments.push({ id: `c-${Math.random().toString(36).slice(2, 9)}`, at: Date.now(), by, text: `Snoozed until ${new Date(until).toLocaleString()}` });
    }
  }

  async scheduleEntries(): Promise<ScheduleEntry[]> { return this.site.schedule; }
  async schedule(): Promise<ScheduleSource> { return this.site.scheduleSource(); }
  async getScheduleDoc(): Promise<ScheduleDoc | null> {
    return {
      version: 1,
      timezone: this.site.topology.timezone,
      default: this.site.scheduleDefault,
      entries: this.site.schedule.map((e) => ({ ...e, days_of_week: [...e.days_of_week] })),
      meta: { ...this.site.scheduleMeta },
    };
  }
  async saveScheduleEntries(entries: ScheduleEntry[]) {
    // Legacy convenience — keep the default + meta untouched.
    this.site.schedule = entries.map((e) => ({ ...e, days_of_week: [...e.days_of_week] }));
    this.site.scheduleMeta = { ...this.site.scheduleMeta, updatedAt: Date.now() };
  }
  async saveScheduleDoc(doc: ScheduleDoc) {
    this.site.schedule = doc.entries.map((e) => ({ ...e, days_of_week: [...e.days_of_week] }));
    this.site.scheduleDefault = {
      mode: doc.default.mode,
      params: { ...(doc.default.params ?? {}) },
    };
    this.site.scheduleMeta = {
      ...doc.meta,
      updatedAt: Date.now(),
    };
    // Schedule a synthetic ACK echo so the StatusEcho UI lights up green.
    const updatedAt = this.site.scheduleMeta.updatedAt!;
    window.setTimeout(() => {
      this._scheduleStatus = {
        echo_kind: "applied",
        applied_updatedAt: updatedAt,
        applied_at_ms: Date.now(),
        applied_entry_count: doc.entries.length,
        rejected_entries: [],
        active_source: "cloud",
        current_mode: this.site.scheduleSource().active_now.mode,
        next_change_at_ms: this.site.scheduleSource().next_change?.at ?? null,
        edge: { service: "mock", version: "0.0.0", host: "mock-host", timezone: doc.timezone },
        stats: { applied: 1 },
      };
    }, 600);
  }
  async getScheduleStatus(): Promise<ScheduleStatus | null> {
    return this._scheduleStatus;
  }
  async getOverrideStatus(): Promise<ScheduleStatus | null> {
    return this._overrideStatus;
  }
  async setOverride(o: OverridePayload | null) {
    this.site.override = o
      ? { mode: o.mode, expires_at: o.expires_at, params: o.params, reason: o.reason }
      : undefined;
    if (o) this.site.activeMode = o.mode;
    const at = Date.now();
    window.setTimeout(() => {
      this._overrideStatus = o
        ? {
            echo_kind: "applied",
            applied_updatedAt: at,
            applied_at_ms: at,
            applied_entry_count: null,
            rejected_entries: [],
            current_mode: o.mode,
            next_change_at_ms: o.expires_at,
          }
        : {
            echo_kind: "deleted",
            applied_updatedAt: at,
            applied_at_ms: at,
            applied_entry_count: null,
            rejected_entries: [],
          };
    }, 400);
  }
  async setActiveMode(mode: ControllerMode) {
    this.site.activeMode = mode;
    this.site.override = { mode, expires_at: Date.now() + 30 * 60_000 };
  }

  /** Latest synthetic schedule echo (null until first save). */
  private _scheduleStatus: ScheduleStatus | null = null;
  /** Latest synthetic override echo (null until first call). */
  private _overrideStatus: ScheduleStatus | null = null;

  // -------------------------------------------------------------------
  // Alarm rule authoring (mock parity)
  // -------------------------------------------------------------------

  /** Seed the rules console with the three rules behind the demo alarms
   *  (see mockGenerator's bootstrapAlarms) so /alarms and /alarms/rules
   *  tell one coherent story instead of "alarms exist, rules are empty".
   *  Same gate as bootstrapAlarms: only the multi-BESS demo site gets
   *  the rich boolean/threshold/inactivity mix. */
  private seedAlarmRules() {
    const topo = this.site.topology;
    if (topo.bess.length < 4) return;
    const now = Date.now();
    const seed = (deviceId: string, rule: AlarmRule) => {
      this.mockRules.set(deviceId, {
        envelope: { version: 4, updatedAt: now, rules: [rule] },
        status: { loaded: 1, errors: [], loaded_at: now },
      });
    };
    // BESS-2 — threshold rule behind "SOC over 95%" (alm-s1-2).
    seed(topo.bess[1].id, {
      id: "rule_socover9_mockseed",
      name: "SOC over 95%",
      enabled: true,
      severity: "MINOR",
      alarm_message: "SOC over 95%",
      deliveryMethod: "notification",
      auto_clear: true,
      propagate: "TENANT",
      type: "threshold",
      datapoint: "battery/measurement/2/SOC",
      config: { operator: ">", threshold: 95, hysteresis: 1, duration_s: 0, unit: "%" },
    });
    // BESS-4 — boolean fault rule behind "Inverter trip" (alm-s1-1).
    seed(topo.bess[3].id, {
      id: "rule_invtrip4_mockseed",
      name: "Inverter trip — DC isolation low",
      enabled: true,
      severity: "CRITICAL",
      alarm_message: "Inverter trip — DC isolation low",
      deliveryMethod: "notification",
      auto_clear: true,
      propagate: "TENANT",
      type: "boolean",
      datapoint: "battery/measurement/4/fault_inverter_trip",
      config: { alarm_when: true, duration_s: 0 },
    });
    // Grid meter — inactivity rule behind "Grid meter inactive 25 min"
    // (alm-s1-3). Keyed on the targetable id ("mock-grid", see
    // listTargetableDevices), not the topology smartmeter id, so the
    // rules page actually lists it.
    seed("mock-grid", {
      id: "rule_gridinac_mockseed",
      name: "Grid meter inactive 25 min",
      enabled: true,
      severity: "WARNING",
      alarm_message: "Grid meter inactive 25 min",
      deliveryMethod: "notification",
      auto_clear: true,
      propagate: "TENANT",
      type: "inactivity",
      datapoint: "smartmeter/measurement/2/power_W",
      config: { timeout_s: 1500 },
    });
  }

  async listAlarmRules(deviceId: string): Promise<AlarmRuleEnvelope> {
    const state = this.mockRules.get(deviceId);
    if (!state) {
      return parseAlarmRuleEnvelope({ version: 2, updatedAt: 0, rules: [] });
    }
    // Deep-clone via JSON roundtrip so callers can't mutate our state.
    return JSON.parse(JSON.stringify(state.envelope)) as AlarmRuleEnvelope;
  }

  async saveAlarmRules(deviceId: string, envelope: AlarmRuleEnvelope): Promise<void> {
    const stamped: AlarmRuleEnvelope = {
      ...envelope,
      updatedAt: Date.now(),
    };
    this.mockRules.set(deviceId, {
      envelope: stamped,
      status: this.mockRules.get(deviceId)?.status ?? null,
    });
    // Schedule a simulated edge ACK ~800 ms later. The cloud-spa UX
    // assumes an asynchronous round-trip and polls
    // `bess_alarm_rules_status` until `loaded_at >= write_ts`.
    const writeTs = stamped.updatedAt;
    window.setTimeout(() => {
      const cur = this.mockRules.get(deviceId);
      if (!cur) return;
      // We accept every rule by default. A "bad" rule could be
      // simulated by returning `errors` here.
      cur.status = {
        loaded: cur.envelope.rules.length,
        errors: [],
        loaded_at: Math.max(writeTs + 1, Date.now()),
      };
    }, 800);
  }

  async getAlarmRulesStatus(deviceId: string): Promise<AlarmRulesStatus | null> {
    return this.mockRules.get(deviceId)?.status ?? null;
  }

  async getAvailableDatapoints(deviceId: string): Promise<AvailableDatapointsEnvelope> {
    // Synthesise a plausible catalog from the mock topology so the
    // datapoint picker has real-looking topics to choose from.
    const topo = this.site.topology;
    const datapoints: DatapointInfo[] = [];
    const bessIdx = topo.bess.findIndex((b) => b.id === deviceId);
    if (bessIdx >= 0) {
      const n = bessIdx + 1;
      const now = Date.now();
      const numeric = (
        topic: string,
        label: string,
        unit: string | null,
        last: number,
        category: string,
      ) => {
        datapoints.push({
          topic,
          data_type: "number",
          last_value: last,
          last_seen_ts: now - 1000,
          sample_count: 600,
          unit,
          label,
          category,
          stale: false,
          observed_range: {
            min: last - Math.abs(last) * 0.2,
            max: last + Math.abs(last) * 0.2,
            p5: last - Math.abs(last) * 0.15,
            p95: last + Math.abs(last) * 0.15,
            count: 200,
          },
        });
      };
      const bool = (topic: string, label: string, last: boolean) => {
        datapoints.push({
          topic,
          data_type: "boolean",
          last_value: last,
          last_seen_ts: now - 1500,
          sample_count: 60,
          unit: null,
          label,
          category: "battery",
          stale: false,
          enum_hints: {
            type: "boolean",
            true_label: topic.includes("fault") ? "Fault active" : "On",
            false_label: topic.includes("fault") ? "OK" : "Off",
          },
        });
      };
      numeric(`battery/measurement/${n}/SOC`, `BESS-${n} State of charge`, "%", 50, "battery");
      numeric(`battery/measurement/${n}/ac_active_power`, `BESS-${n} Active power`, "W", 0, "battery");
      numeric(`battery/measurement/${n}/ambient_temperature`, `BESS-${n} Ambient temperature`, "°C", 24, "battery");
      numeric(`battery/measurement/${n}/Total_Voltage`, `BESS-${n} DC bus voltage`, "V", 810, "battery");
      bool(`battery/measurement/${n}/inverter_fault`, `BESS-${n} Inverter fault`, false);
      bool(`controller/${n}/output/BESS_ONOFF`, `BESS-${n} On/Off command`, true);
    } else {
      // Grid / controller / aux fallback — just a couple of topics so
      // the picker isn't empty.
      const now = Date.now();
      datapoints.push({
        topic: "smartmeter/aggregation/grid_W",
        data_type: "number",
        last_value: 12500,
        last_seen_ts: now - 1000,
        sample_count: 600,
        unit: "W",
        label: "Grid active power",
        category: "smartmeter",
        stale: false,
      });
      datapoints.push({
        topic: "controller/1/output/power_W",
        data_type: "number",
        last_value: -3000,
        last_seen_ts: now - 1500,
        sample_count: 600,
        unit: "W",
        label: "Controller setpoint",
        category: "controller",
        stale: false,
      });
    }
    return {
      version: 2,
      updatedAt: Date.now(),
      engine_version: "3.0.0-mock",
      datapoints,
    };
  }

  async listTargetableDevices(): Promise<AlarmTargetableDevice[]> {
    const out: AlarmTargetableDevice[] = [];
    this.site.topology.bess.forEach((b, idx) =>
      out.push({ id: b.id, name: b.name || `BESS-${idx + 1}`, kind: "bess" }),
    );
    out.push({ id: "mock-grid", name: "Grid meter", kind: "grid" });
    out.push({ id: "mock-controller", name: "Controller", kind: "controller" });
    return out;
  }

  // ------------------------------------------------------------------
  // Wholesale-electricity bidding zones (Market price page)
  // ------------------------------------------------------------------
  //
  // Mock mode synthesises one zone ("Demo / IT_NORD") with a realistic
  // 24h day-ahead curve, a 14-day rolling history, derived stats, and
  // freshness metadata so the page can be developed and demoed without
  // a live TB.

  async discoverBiddingZones(): Promise<{ zones: ZoneAsset[]; scoped: boolean }> {
    return {
      zones: [{ id: MOCK_ZONE_ID, name: "IT_NORD", label: "Italy North (demo)" }],
      scoped: false,
    };
  }

  async getZoneAttributes(
    zoneId: string,
    keys: readonly string[],
  ): Promise<ZoneAttributeEntry[]> {
    if (zoneId !== MOCK_ZONE_ID) return [];
    const today = isoDate(0);
    const tomorrow = isoDate(1);
    const tzLocal = "Europe/Rome";
    const next = mockEnvelope(tomorrow, tzLocal);
    const intraday = mockEnvelope(today, tzLocal, true);
    const ring: ReturnType<typeof mockEnvelope>[] = [];
    for (let d = 13; d >= 0; d--) {
      ring.push(mockEnvelope(isoDate(-d), tzLocal));
    }
    const stats = mockStats(next.values);
    const ring7 = ring.slice(-8, -1).flatMap((e) => e.values).filter((v): v is number => v !== null);
    const avg7 = ring7.length ? ring7.reduce((s, x) => s + x, 0) / ring7.length : stats.avg;
    const vs7 = avg7 ? (stats.avg - avg7) / avg7 : 0;
    const all: Record<string, unknown> = {
      next_day_prices: next,
      next_day_min: stats.min,
      next_day_max: stats.max,
      next_day_avg: stats.avg,
      next_day_std: stats.std,
      next_day_peak_hour: stats.peakHour,
      next_day_trough_hour: stats.troughHour,
      next_day_low3_hours: stats.low3Hours,
      next_day_high3_hours: stats.high3Hours,
      next_day_vs_7d_avg: vs7,
      latest_intraday_prices: intraday,
      recent_days_ring: ring,
      data_freshness: "fresh",
      last_successful_fetch: new Date().toISOString(),
      last_intraday_fetch: new Date().toISOString(),
    };
    return keys.map((k) => ({ key: k, value: all[k] ?? null }));
  }

  async getZoneTimeseries(
    zoneId: string,
    keys: readonly string[],
    opts: { startTs: number; endTs: number; limit?: number },
  ): Promise<Record<string, ZoneTsPoint[]>> {
    if (zoneId !== MOCK_ZONE_ID) return {};
    const out: Record<string, ZoneTsPoint[]> = {};
    for (const k of keys) {
      if (k === "price_eur_mwh" || k === "intraday_price_eur_mwh") {
        out[k] = mockHourlyPriceSeries(opts.startTs, opts.endTs);
      } else {
        out[k] = [];
      }
    }
    return out;
  }

  // ------------------------------------------------------------------
  // Per-site SERVER_SCOPE attribute storage (localStorage-backed)
  // ------------------------------------------------------------------

  private attrStorageKey(key: string): string {
    return `ems-site-attr:${this.site.topology.id}:${key}`;
  }

  async getSiteAttribute(key: string): Promise<string | number | boolean | null> {
    try {
      const raw = localStorage.getItem(this.attrStorageKey(key));
      if (raw == null) return null;
      const parsed = JSON.parse(raw) as unknown;
      if (parsed === null) return null;
      if (
        typeof parsed === "string" ||
        typeof parsed === "number" ||
        typeof parsed === "boolean"
      ) {
        return parsed;
      }
      return null;
    } catch {
      return null;
    }
  }

  async setSiteAttribute(
    key: string,
    value: string | number | boolean | null,
  ): Promise<void> {
    try {
      const k = this.attrStorageKey(key);
      if (value === null) localStorage.removeItem(k);
      else localStorage.setItem(k, JSON.stringify(value));
    } catch {
      /* private-mode / quota — silently no-op to preserve the contract. */
    }
  }

  /**
   * Tear down listeners and timers. Parity with TbAdapter.destroy so the
   * store can call it uniformly on site/mode swaps without conditionals.
   */
  destroy(): void {
    this.listeners.clear();
    this.stop();
    this.mockRules.clear();
  }

  private start() {
    this.timer = window.setInterval(() => {
      // Simulated outage: skip the sim step (values + ts freeze at the
      // outage start) but keep notifying so the UI sees the connection
      // field degrade stale → offline in real time.
      if (this.outageStartedAt === null) this.site.advance(this.tickMs);
      const snap = this.snapshotNow();
      for (const h of this.listeners) h(snap);
    }, this.tickMs);
  }
  private stop() {
    if (this.timer != null) window.clearInterval(this.timer);
    this.timer = null;
  }
}

function bucketStep(g: EnergyGran): number {
  switch (g) {
    case "15m": return 15 * 60_000;
    case "h":   return 3_600_000;
    case "d":   return 24 * 3_600_000;
    case "w":   return 7 * 24 * 3_600_000;
    case "m":   return 30 * 24 * 3_600_000;
    case "y":   return 365 * 24 * 3_600_000;
  }
}

// ---------------------------------------------------------------------------
// Market price mock helpers
// ---------------------------------------------------------------------------

/** Single synthetic zone id surfaced by the mock adapter. */
const MOCK_ZONE_ID = "mock-zone-it-nord";

/** ISO yyyy-mm-dd offset by `dayOffset` from today (0 = today, 1 = tomorrow). */
function isoDate(dayOffset: number): string {
  const d = new Date();
  d.setDate(d.getDate() + dayOffset);
  return d.toISOString().slice(0, 10);
}

/**
 * Synthesise a 24-value day-ahead curve in EUR/MWh. The shape follows a
 * realistic Italian-grid pattern: night trough (~3-4 am), morning peak
 * (~8-9), midday solar dip, evening peak (~18-20). A small random offset
 * per day keeps consecutive days from looking identical. `intraday`
 * leaves recent hours `null` to mimic AE2's sparse intraday envelope.
 */
function mockEnvelope(date: string, timezone: string, intraday = false): {
  date: string;
  timezone: string;
  unit: string;
  currency: string;
  values: Array<number | null>;
  fetched_at: string;
  source: string;
} {
  // Stable per-day jitter so the same date always produces the same curve.
  const seed = date.split("-").reduce((s, p) => s * 31 + Number(p), 0);
  const rand = (i: number): number => {
    const x = Math.sin(seed * 131 + i * 17.7) * 10000;
    return x - Math.floor(x); // [0, 1)
  };
  const values: Array<number | null> = [];
  for (let h = 0; h < 24; h++) {
    // Two-peak daily cycle. Night low ~50, morning peak ~140 @ h=8,
    // midday dip ~85 @ h=13, evening peak ~155 @ h=19.
    const morning = 90 * Math.exp(-Math.pow((h - 8) / 2.5, 2));
    const evening = 105 * Math.exp(-Math.pow((h - 19) / 2.5, 2));
    const midday = -25 * Math.exp(-Math.pow((h - 13) / 2.0, 2));
    const base = 55 + morning + evening + midday;
    const noise = (rand(h) - 0.5) * 14;
    let v = Math.max(8, Math.round((base + noise) * 100) / 100);
    // Intraday: leave the hours BEFORE "now" null (AE2 only publishes
    // intraday for the hours the auction has cleared).
    if (intraday && h < new Date().getHours()) v = null as unknown as number;
    values.push(v as number | null);
  }
  return {
    date,
    timezone,
    // Denominator only — the page composes `${currency}/${unit}`, so
    // publishing "EUR/MWh" here would render a doubled "EUR/EUR/MWh".
    unit: "MWh",
    currency: "EUR",
    values,
    fetched_at: new Date().toISOString(),
    source: "mock",
  };
}

/** Derived stats over a (possibly sparse) day-ahead curve. The hour
 *  lists are `{hour, price}` objects to match the page's HourPrice
 *  contract (plain hour indices rendered as "undefined:00–NaN:00"). */
function mockStats(values: Array<number | null>): {
  min: number; max: number; avg: number; std: number;
  peakHour: number; troughHour: number;
  low3Hours: Array<{ hour: number; price: number }>;
  high3Hours: Array<{ hour: number; price: number }>;
} {
  const vals: Array<{ h: number; v: number }> = [];
  values.forEach((v, h) => { if (v !== null && Number.isFinite(v)) vals.push({ h, v }); });
  if (vals.length === 0) {
    return { min: 0, max: 0, avg: 0, std: 0, peakHour: 0, troughHour: 0, low3Hours: [], high3Hours: [] };
  }
  const sorted = [...vals].sort((a, b) => a.v - b.v);
  const min = sorted[0].v;
  const max = sorted[sorted.length - 1].v;
  const avg = vals.reduce((s, x) => s + x.v, 0) / vals.length;
  const variance = vals.reduce((s, x) => s + (x.v - avg) ** 2, 0) / vals.length;
  return {
    min, max, avg, std: Math.sqrt(variance),
    peakHour: sorted[sorted.length - 1].h,
    troughHour: sorted[0].h,
    low3Hours: sorted.slice(0, 3).map((x) => ({ hour: x.h, price: x.v })).sort((a, b) => a.hour - b.hour),
    high3Hours: sorted.slice(-3).map((x) => ({ hour: x.h, price: x.v })).sort((a, b) => a.hour - b.hour),
  };
}

/**
 * Hourly price timeseries between `startTs` and `endTs`. Each point's
 * value comes from the same daily-cycle synth as the envelope, so the
 * 14-day rolling history chart agrees with the per-day envelopes.
 */
function mockHourlyPriceSeries(startTs: number, endTs: number): ZoneTsPoint[] {
  const out: ZoneTsPoint[] = [];
  const HOUR = 3_600_000;
  const start = Math.floor(startTs / HOUR) * HOUR;
  for (let ts = start; ts <= endTs; ts += HOUR) {
    const d = new Date(ts);
    const dayKey = d.toISOString().slice(0, 10);
    const seed = dayKey.split("-").reduce((s, p) => s * 31 + Number(p), 0);
    const h = d.getHours();
    const rand = (Math.sin(seed * 131 + h * 17.7) * 10000) - Math.floor(Math.sin(seed * 131 + h * 17.7) * 10000);
    const morning = 90 * Math.exp(-Math.pow((h - 8) / 2.5, 2));
    const evening = 105 * Math.exp(-Math.pow((h - 19) / 2.5, 2));
    const midday = -25 * Math.exp(-Math.pow((h - 13) / 2.0, 2));
    const base = 55 + morning + evening + midday;
    const noise = (rand - 0.5) * 14;
    out.push({ ts, value: Math.max(8, Math.round((base + noise) * 100) / 100) });
  }
  return out;
}
